import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter } from "react-router-dom";
import { lazy, Suspense, useState, useEffect, ReactNode } from "react";

import { ClerkProviderWrapper } from "@/components/ClerkProviderWrapper";
import { AnimatedRoutes } from "@/components/AnimatedRoutes";

// Lazy load non-critical UI components to reduce initial JS bundle
const ScrollProgressIndicator = lazy(() => import("@/components/ScrollProgressIndicator"));
const BackToTopButton = lazy(() => import("@/components/BackToTopButton"));
const CookieConsentBanner = lazy(() => import("@/components/CookieConsentBanner"));

const queryClient = new QueryClient();

/**
 * DeferredProviders — loads MembersAuthProvider and AuditProvider asynchronously
 * to keep the Supabase SDK (~45 KB) and mock data off the critical rendering path.
 * Children render immediately; providers wrap them once loaded.
 */
function DeferredProviders({ children }: { children: ReactNode }) {
  const [Providers, setProviders] = useState<React.ComponentType<{ children: ReactNode }> | null>(null);

  useEffect(() => {
    Promise.all([
      import("./contexts/MembersAuthContext"),
      import("./contexts/AuditContext"),
    ]).then(([membersModule, auditModule]) => {
      const MembersAuthProvider = membersModule.MembersAuthProvider;
      const AuditProvider = auditModule.AuditProvider;
      setProviders(() => ({ children: inner }: { children: ReactNode }) => (
        <AuditProvider>
          <MembersAuthProvider>{inner}</MembersAuthProvider>
        </AuditProvider>
      ));
    });
  }, []);

  if (Providers) {
    return <Providers>{children}</Providers>;
  }

  // Check if current path needs auth providers before rendering
  const needsProviders = typeof window !== 'undefined' &&
    (window.location.pathname.startsWith('/admin') ||
     window.location.pathname.startsWith('/members') ||
     window.location.pathname.startsWith('/dashboard') ||
     window.location.pathname.startsWith('/board'));

  if (needsProviders) {
    // Wait for providers on protected routes to avoid useMembersAuth crash
    return null;
  }

  // Public routes render immediately for fast LCP
  return <>{children}</>;
}

const App = () => (
  <ClerkProviderWrapper>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <BrowserRouter>
          <DeferredProviders>
            <Suspense fallback={null}>
              <ScrollProgressIndicator />
            </Suspense>
            <Toaster />
            <Sonner />
            <AnimatedRoutes />
            <Suspense fallback={null}>
              <BackToTopButton />
              <CookieConsentBanner />
            </Suspense>
          </DeferredProviders>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ClerkProviderWrapper>
);

export default App;
